import ormaApi from './OrmaApi';

/**
 * Order devices in bulk for a group
 * @param {string} groupId - Group ID for bulk ordering
 * @param {Array<{type: string, quantity: number}>} devices - Array of device types and quantities
 * @param {Object} shippingInfo - Shipping information
 * @param {string} shippingInfo.address - Shipping address
 * @param {string} shippingInfo.city - City
 * @param {string} shippingInfo.state - State
 * @param {string} shippingInfo.zipCode - ZIP code
 * @returns {Promise<Object>} Order response with summary and results
 */
export const orderBulkDevice = async (
  groupId,
  devices,
  shippingInfo,
) => {
  try {
    const response = await ormaApi.post('/api/devices/order-bulk', {
      groupId,
      devices,
      shippingInfo,
    });
    return response.data;
  } catch (error) {
    console.error('Error ordering bulk devices:', error);
    throw error;
  }
};

/**
 * Get order history for a group with pagination
 * @param {string} groupId - Group ID to fetch order history for
 * @param {number} skip - Number of records to skip
 * @param {number} limit - Maximum number of records to return
 * @returns {Promise<Array>} Array of order history items
 */
export const getOrderHistory = async (
  groupId,
  skip = 0,
  limit = 50,
) => {
  try {
    const response = await ormaApi.post(
      '/api/devices/order-history',
      {
        groupId,
        skip,
        limit,
      },
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching order history:', error);
    throw error;
  }
};

/**
 * Super-admin device assignment / refill tracking
 * @param {Object} filters - tab ('devices'|'refills'), view ('orders'|'assignments'|'refills'),
 *   yearMonth, timeZone, ownerIds, vendor, deviceType, search, skip, limit
 * @returns {Promise<{items, count, stats}>}
 */
export const fetchAssignmentTracking = async (filters = {}) => {
  try {
    const response = await ormaApi.post(
      '/api/devices/assignment-tracking',
      filters,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching assignment tracking:', error);
    throw error;
  }
};

/**
 * Refresh a single Manage Devices row from Tenovi / BodyTrace.
 * @param {{ id: string, vendor: 'Tenovi'|'BodyTrace' }} payload
 * @returns {Promise<{success, item, refreshedFrom, updated, message}>}
 */
export const refreshAssignmentTrackingItem = async ({ id, vendor }) => {
  try {
    const response = await ormaApi.post(
      '/api/devices/assignment-tracking/refresh',
      { id, vendor },
    );
    return response.data;
  } catch (error) {
    console.error('Error refreshing assignment tracking item:', error);
    throw error;
  }
};
